
import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/hooks/useAuth';
import { useLocation } from 'react-router-dom';

const isIOS = () => {
  // Fix: Using type assertion to handle the MSStream property
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
};

const isInStandaloneMode = () => {
  return window.matchMedia('(display-mode: standalone)').matches || 
         (window.navigator as any).standalone || 
         document.referrer.includes('android-app://');
};

const PWAInstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const isMobile = useIsMobile();
  const { user } = useAuth();
  const location = useLocation();
  
  // Check if we're on a public page (event publish pages or homepage)
  const isPublicPage = location.pathname === '/' || location.pathname.startsWith('/events/publish/');
  
  useEffect(() => {
    // Only show for iOS devices that aren't already installed, 
    // when user is authenticated, and not on public pages
    if (isIOS() && !isInStandaloneMode() && isMobile && user && !isPublicPage) {
      // Show the prompt after 3 seconds of being on the site
      const timer = setTimeout(() => {
        setShowPrompt(true);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [isMobile, user, isPublicPage]);
  
  if (!showPrompt) return null;
  
  return (
    <div className="fixed bottom-0 left-0 right-0 bg-purple-dark/95 p-4 z-50 border-t border-purple-light/30 shadow-lg animate-in slide-in-from-bottom duration-300">
      <div className="relative">
        <button 
          onClick={() => setShowPrompt(false)}
          className="absolute right-0 top-0 text-muted-foreground"
          aria-label="Close"
        >
          <X size={20} />
        </button>
        
        <div className="flex flex-col space-y-2">
          <h3 className="font-semibold text-sm text-cta">Install Dicole.AI on your device</h3>
          <p className="text-xs text-muted-foreground">For the best experience, add this app to your home screen:</p>
          
          <div className="flex items-center space-x-2 mt-1">
            <div className="h-8 w-8 rounded-md bg-cta flex items-center justify-center">
              <img 
                src="/pwa-icons/icon-72x72.png" 
                alt="Dicole.AI app icon" 
                className="h-6 w-6"
              />
            </div>
            <div className="text-xs">
              <p className="font-medium text-foreground">Dicole.AI</p>
              <p className="text-muted-foreground">AI-Powered Event Summaries</p>
            </div>
          </div>
          
          <div className="text-xs text-muted-foreground mt-2">
            <p className="mb-1">1. Tap <span className="inline-flex items-center justify-center border border-muted-foreground/30 rounded px-1 mx-1">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 4V20M20 12L4 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </span> at the bottom of your browser</p>
            <p className="mb-1">2. Scroll down and tap "Add to Home Screen"</p>
            <p>3. Tap "Add" in the top right corner</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
